import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const mapicCannesEn: ChauffeurPage = {
  slug: "mapic-cannes",
  title: "Private chauffeur MAPIC Cannes – B2B VTC & shuttles",
  subtitle:
    "Private chauffeur during MAPIC in Cannes: B2B shuttles, Nice airport meet-and-greet and daily hire for delegations around the Palais des Festivals.",
  keywords: [
    "mapic cannes chauffeur",
    "mapic vtc",
    "palais des festivals shuttle mapic",
  ],
  hero: {
    title: "Private chauffeur during MAPIC in Cannes",
    subtitle:
      "The international retail-property market in November: a dedicated chauffeur for your B2B meetings around the Palais des Festivals.",
    keywords: [
      "MAPIC chauffeur",
      "Cannes trade-show VTC",
      "Palais des Festivals shuttle",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during MAPIC in Cannes",
    badges: ["VIP airport welcome", "B2B shuttles", "Hourly hire"],
  },
  benefits: {
    heading: "Why a chauffeur during MAPIC",
    items: [
      {
        title: "B2B trade-show logistics",
        description:
          "Back-to-back meetings, stands and business dinners: the chauffeur chains your journeys to keep a dense professional agenda on time.",
      },
      {
        title: "Nice airport welcome",
        description:
          "Many delegations arrive the day before. Name-board welcome, flight tracking and a direct transfer to your Cannes hotel.",
      },
      {
        title: "Palais des Festivals at hand",
        description:
          "Drop-off as close as possible to the Palais entrances and exhibition halls, depending on the show's crowds.",
      },
      {
        title: "Team & partner shuttles",
        description:
          "Multi-vehicle coordination for exhibitors, clients and partners between hotels, the Palais and restaurants.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during MAPIC",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Cannes transfer with flight tracking across the MAPIC days.",
      },
      {
        title: "Hotel ↔ Palais shuttles",
        text: "Rotations between your hotels, the Palais des Festivals and your meeting venues.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur by the hour or day for a busy B2B agenda.",
      },
      {
        title: "Business dinners & evenings",
        text: "Croisette and hinterland restaurants, with a guaranteed late-night return.",
      },
      {
        title: "Inter-city transfers",
        text: "Links to Monaco, Nice or Antibes for meetings and trip extensions.",
      },
    ],
  },
  locations: {
    heading: "Key points during MAPIC",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Exhibition halls and conference spaces of the show.",
      },
      {
        name: "La Croisette",
        description:
          "Business hotels (Carlton, Martinez, Majestic) and networking venues.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, the main delegation arrivals.",
      },
      {
        name: "Business district & hotels",
        description: "Properties hosting exhibitors and professional visitors.",
      },
      {
        name: "Mougins & hinterland",
        description: "Business dinners and evenings outside the centre.",
      },
    ],
  },
  generic: {
    heading: "MAPIC from a chauffeur's seat",
    subheading:
      "In November, the Palais des Festivals hosts the international retail-property market and a strictly professional audience.",
    paragraphs: [
      "Every November, MAPIC gathers developers, retail brands and investors of retail and commercial property at the Palais des Festivals in Cannes. The days chain meetings, stands and dinners: a dedicated chauffeur saves precious time between the hotel, the Palais and the networking venues.",
      "Delegations arrive mainly through Nice Côte d'Azur airport. With flight tracking and a name-board welcome, we absorb delays and drop your teams straight at the hotel, ready for the first meeting.",
      "The show is all about the agenda: hourly hire keeps the same chauffeur through the day, coordinates several vehicles for your partners and secures the returns after business dinners.",
    ],
    bullets: [
      "Hotel ↔ Palais shuttles",
      "Flight tracking in November",
      "Multi-vehicle B2B coordination",
    ],
  },
  process: {
    heading: "Booking a chauffeur for MAPIC",
    steps: [
      "Tell us your dates, your hotels and your schedule of professional meetings.",
      "We organise the shuttles between hotels, the Palais des Festivals and networking venues.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Hourly hire or multi-vehicle coordination for the whole delegation.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur for MAPIC in Cannes?",
      a: "Book in advance: in November, MAPIC concentrates a large B2B audience. Share your dates, your hotels and your agenda so we can arrange a dedicated chauffeur or several vehicles for your delegation.",
    },
    {
      q: "Do you provide shuttles between the hotel and the Palais des Festivals?",
      a: "Yes. We organise rotations between your hotels, the Palais des Festivals and your meeting venues to keep a tight trade-show agenda running smoothly.",
    },
    {
      q: "Do you handle arrivals at Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Most delegations arrive through Nice Côte d'Azur; we adjust pick-ups to schedules and delays.",
    },
    {
      q: "Can you coordinate several vehicles for a delegation?",
      a: "Yes. For exhibitors and teams, we coordinate several vehicles between hotels, the Palais and restaurants, with a single point of contact.",
    },
    {
      q: "Do you offer hire for business dinners?",
      a: "Yes. The chauffeur stays available in the evening for your dinners on the Croisette or in the hinterland and secures the late-night return.",
    },
    {
      q: "Why choose KS Rent Services for MAPIC?",
      a: "Our private chauffeur service on the French Riviera is rated 5.0/5 from 38 Google reviews: punctuality, discretion and knowledge of the Palais des Festivals, essential for a professional audience.",
    },
  ],
  relatedLinks: [
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
    { label: "Business chauffeur", href: "/en/chauffeur/business" },
    { label: "Nice Airport rates and routes", href: "/en/chauffeur/airport" },
    { label: "Rent a car Cannes", href: "/en/location/car-rental-cannes" },
  ],
  cta: {
    title: "Your chauffeur for MAPIC in Cannes",
    text: "B2B shuttles, airport welcome and business dinners: book a dedicated chauffeur for the show.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "MAPIC Cannes Chauffeur — B2B car service & shuttles",
    description:
      "Private chauffeur during MAPIC in Cannes: B2B shuttles between hotels and the Palais des Festivals, Nice airport meet-and-greet and hourly hire. Book now.",
    canonical: "/en/chauffeur/mapic-cannes",
  },
};

export default mapicCannesEn;
